import Intercom, { shutdown } from '@intercom/messenger-js-sdk'
import { useEffect } from 'react'
import { useCurrentUserQuery, useIntercomTokenQuery } from '../hooks/api'

const APP_ID = import.meta.env.VITE_INTERCOM_APP_ID

/**
 * Boots the Intercom Messenger (which surfaces Fin) on every studio route.
 *
 * Logged-out visitors (login screen, public routes) get an anonymous boot so
 * Fin is reachable everywhere. Once the user is logged in and the server-signed
 * JWT (`intercom_user_jwt`) loads, we re-boot with their identity so Intercom
 * can trust it — `name` is a non-sensitive attribute, passed outside the token.
 *
 * Auth transitions shut down the current session before re-booting, per
 * Intercom's guidance, so an anonymous conversation never merges into the wrong
 * identity. Renders nothing; mount once near the app root.
 */
export function IntercomMessenger() {
  const { data: user } = useCurrentUserQuery()
  const { data: token } = useIntercomTokenQuery({ enabled: !!user })

  useEffect(() => {
    if (!APP_ID) {
      if (import.meta.env.DEV) {
        console.warn('VITE_INTERCOM_APP_ID is not set, so Intercom Messenger is disabled')
      }
      return
    }

    Intercom(
      user && token
        ? { app_id: APP_ID, intercom_user_jwt: token, name: user.name ?? undefined }
        : { app_id: APP_ID }
    )

    return () => shutdown()
  }, [user, token])

  return null
}
